import {useAui} from '@assistant-ui/react';
import {
  Banner,
  Button,
  CalloutIcon,
  Dialog,
  DotProgress,
  Icon,
  IconButton,
} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {Virtuoso} from 'react-virtuoso';

import {useStoreSelector} from '../../../agentChat/store/context';
import {
  selectHistoryItems,
  selectThreadDisplayTitle,
} from '../../../agentChat/store/selectors';
import type {AgentChatState, Thread} from '../../../agentChat/store/thread';
import {useNavContext} from '../../NavContextProvider';
import {groupItemsByTimeBucket} from './grouping';
import {HistoryItem, type HistoryItemMode} from './HistoryItem';
import {useDeleteThread} from './useDeleteThread';
import {useRenameThread} from './useRenameThread';
import {useThreadHistory} from './useThreadHistory';

type Row = string | Thread;

type PendingAction = {threadId: string; type: 'rename' | 'delete'} | null;

interface VirtuosoContext {
  status: 'idle' | 'loading' | 'error';
  loadMore: () => void;
}

export function WBAgentChatHistoryPopover() {
  const [open, setOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const aui = useAui();
  const {params} = useNavContext();
  const currentEntity = params.entityName ?? null;
  const renameThread = useRenameThread();
  const deleteThread = useDeleteThread();

  const {ids, status, loadInitial, loadMore, cancelFetch} = useThreadHistory();

  const historyItems = useStoreSelector(
    useCallback((s: AgentChatState) => selectHistoryItems(s, ids), [ids]),
    shallowEqual
  );
  const displayTitles = useStoreSelector(
    useCallback(
      (s: AgentChatState) =>
        Object.fromEntries(
          ids.map(id => [id, selectThreadDisplayTitle(s, id)])
        ),
      [ids]
    ),
    shallowEqual
  );
  const hasItems = historyItems.length > 0;

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);

      if (isOpen) {
        loadInitial();
      } else {
        cancelFetch();
        setPendingAction(null);
      }
    },
    [loadInitial, cancelFetch]
  );

  const handleSelect = useCallback(
    (rootTurnId: string) => {
      handleOpenChange(false);
      aui.threads().switchToThread(rootTurnId);
    },
    [aui, handleOpenChange]
  );

  const handleToggle = useCallback(
    () => handleOpenChange(!open),
    [handleOpenChange, open]
  );

  const handleStartRename = useCallback((threadId: string) => {
    setPendingAction({threadId, type: 'rename'});
  }, []);

  const handleCommitRename = useCallback(
    (threadId: string, newTitle: string) => {
      setPendingAction(null);
      void renameThread(threadId, newTitle);
    },
    [renameThread]
  );

  const handleStartDelete = useCallback((threadId: string) => {
    setPendingAction({threadId, type: 'delete'});
  }, []);

  const handleConfirmDelete = useCallback(
    (threadId: string) => {
      setPendingAction(null);
      void deleteThread(threadId);
    },
    [deleteThread]
  );

  const handleCancelMode = useCallback(() => {
    setPendingAction(null);
  }, []);

  const rows = useMemo<Row[]>(() => {
    const groups = groupItemsByTimeBucket(
      historyItems,
      thread => new Date(thread.createdAt),
      new Date()
    );
    return [...groups].flatMap(([label, groupItems]) => [label, ...groupItems]);
  }, [historyItems]);

  const itemContent = useCallback(
    (index: number) => {
      const row = rows[index];

      // A bucket label from 'groupItemsByTimeBucket'.
      if (typeof row === 'string') {
        return (
          <div>
            {index > 0 && (
              <div className="h-[13px] py-[6px] -mx-[6px]">
                <div className="h-px bg-moon-250 dark:bg-[#363C44]" />
              </div>
            )}
            <div className="h-[32px] py-[4px] px-[4px] flex items-center justify-between text-base font-semibold">
              {row}
            </div>
          </div>
        );
      }

      const mode: HistoryItemMode =
        pendingAction?.threadId === row.id
          ? pendingAction.type === 'rename'
            ? 'renaming'
            : 'confirming-delete'
          : 'idle';

      return (
        <HistoryItem
          thread={row}
          displayTitle={displayTitles[row.id]}
          currentEntity={currentEntity}
          mode={mode}
          onSelect={handleSelect}
          onStartRename={handleStartRename}
          onCommitRename={handleCommitRename}
          onStartDelete={handleStartDelete}
          onConfirmDelete={handleConfirmDelete}
          onCancelMode={handleCancelMode}
        />
      );
    },
    [
      rows,
      displayTitles,
      currentEntity,
      pendingAction,
      handleSelect,
      handleStartRename,
      handleCommitRename,
      handleStartDelete,
      handleConfirmDelete,
      handleCancelMode,
    ]
  );

  const virtuosoContext = useMemo<VirtuosoContext>(
    () => ({status, loadMore}),
    [status, loadMore]
  );

  const isEmpty = !hasItems && status === 'idle';
  const isInitialError = !hasItems && status === 'error';
  const isInitialLoading = !hasItems && status === 'loading';

  return (
    <>
      <IconButton
        ref={triggerRef}
        variant="ghost"
        icon="recent-clock"
        aria-label="history"
        onClick={handleToggle}
        tooltip={{children: 'Chat history'}}
      />

      <Dialog.Popover
        aria-label="Chat history"
        isOpen={open}
        onOpenChange={handleOpenChange}
        placement="bottom end"
        triggerRef={triggerRef}>
        <Tailwind>
          <div className="w-[313px] flex flex-col max-h-[407px] py-[6px] overflow-hidden bg-white dark:bg-moon-850 text-moon-800 dark:text-moon-200">
            {isInitialLoading && (
              <div className="flex items-center justify-center py-32">
                <DotProgress aria-label="Loading history" />
              </div>
            )}

            {isEmpty && (
              <div className="flex flex-col items-center justify-center gap-8 py-32 text-moon-500 dark:text-moon-400">
                <Icon icon="forum-chat-bubble" size="xl" />
                <span className="text-sm">No previous conversations</span>
              </div>
            )}

            {isInitialError && (
              <div className="flex flex-col justify-center items-center py-32 px-16 gap-12">
                <CalloutIcon icon="warning" color="red" size="md" />
                <span className="font-semibold text-base">
                  Failed to load conversations
                </span>
                <Button
                  aria-label="Retry loading conversations"
                  onPress={loadInitial}
                  startIcon="reload-refresh"
                  variant="secondary">
                  Retry
                </Button>
              </div>
            )}

            {hasItems && (
              <Virtuoso
                aria-label="Past conversations"
                components={virtuosoComponents}
                context={virtuosoContext}
                endReached={loadMore}
                itemContent={itemContent}
                overscan={50}
                style={{height: 395}}
                totalCount={rows.length}
              />
            )}
          </div>
        </Tailwind>
      </Dialog.Popover>
    </>
  );
}

function VirtuosoFooter({context}: {context?: VirtuosoContext}) {
  if (context?.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-8">
        <DotProgress aria-label="Loading more" />
      </div>
    );
  }

  if (context?.status === 'error') {
    return (
      <div className="w-full px-10 py-8">
        <Banner
          aria-label="Failed to load more conversations"
          isDismissible={false}
          isVisible
          size="md"
          variant="error">
          <Banner.Body>Failed to load more.</Banner.Body>
          <Banner.Action
            aria-label="Retry loading more conversations"
            onPress={context.loadMore}>
            Retry
          </Banner.Action>
        </Banner>
      </div>
    );
  }

  return null;
}

const VirtuosoList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function VirtuosoList({children, style, ...props}, ref) {
  return (
    <div ref={ref} {...props} className="px-[6px]" style={style}>
      {children}
    </div>
  );
});

const virtuosoComponents = {
  Footer: VirtuosoFooter,
  List: VirtuosoList,
};
